import { CONCIERGE_VISIBLE, ONBOARDING_DEMO_VISIBLE, PRICING_V2_VISIBLE, SAVINGS_CALCULATOR_VISIBLE } from '../../config/featureFlags';
import { can, getAccess, isGroupOwnerHere, useAccess } from '../permissions/access';
import { useState, useEffect, useCallback } from 'react';

const ALL_TABS = [
    {
        id: 'payout-account',
        label: 'Payouts',
        path: '/settings/payout-account',
        roles: ['owner', 'selfEmployed'],
        receptionKey: 'payoutAccount',
    },
    {
        id: 'card-reader',
        label: 'Payments',
        path: '/settings/card-reader',
        // Pairing / removing the salon's readers is a settings change (audit
        // 2026-10-02 C2); a self-employed stylist runs their own.
        roles: ['owner', 'selfEmployed'],
        cap: 'settings',
        receptionKey: 'cardReader',
    },
    {
        id: 'salon-details',
        label: 'Business',
        path: '/settings/salon-details',
        roles: ['owner'],
        cap: 'settings', // roles & permissions: or anyone whose role has it
        receptionKey: 'salonDetails',
    },
    {
        id: 'booking-url',
        label: 'Bookings',
        path: '/settings/booking-url',
        roles: ['owner', 'selfEmployed'],
        cap: 'settings', // roles & permissions: or anyone whose role has it
        receptionKey: 'bookingUrl',
    },
    {
        id: 'billing',
        // Pricing (Oct 2026): plan, usage and billing are one page for the owner.
        label: PRICING_V2_VISIBLE ? 'Plan & billing' : 'Billing',
        path: '/settings/billing',
        roles: ['owner', 'selfEmployed'],
        receptionKey: 'billing',
    },
    {
        id: 'diary-sharing',
        // Bookings, services, client and marketing sharing on one page (Oct
        // 2026); for a self-employed stylist it replaced Your choices.
        label: 'Sharing',
        path: '/settings/sharing',
        roles: ['owner', 'selfEmployed'],
        cap: 'settings', // roles & permissions: or anyone whose role has it
        receptionKey: 'diarySharing',
    },
    {
        id: 'notifications',
        label: 'Notifications',
        path: '/settings/notifications',
        roles: ['all'],
        receptionKey: 'notificationSettings',
    },
    {
        id: 'locations',
        label: 'Locations',
        path: '/settings/locations',
        // The whole business's owner only: a linked site's account holder
        // can't add or close locations (the server refuses them).
        roles: ['groupOwner'],
        receptionKey: 'locations',
    },
    {
        id: 'reception-mode',
        label: 'Reception',
        path: '/settings/reception-mode',
        roles: ['owner'],
        cap: 'settings', // roles & permissions: or anyone whose role has it
        receptionKey: null, // always visible for owners
    },
    // A location's phone numbers: Bailey answers calls on them, and the
    // mobile one is the two-way texts number. Same flag as the receptionist.
    ...(CONCIERGE_VISIBLE ? [{
        id: 'business-numbers',
        label: 'Business numbers',
        path: '/settings/business-numbers',
        roles: ['owner'],
        cap: 'settings',
        capOnly: true,
        receptionKey: 'aiReceptionist',
        hideInReception: true,
    }] : []),
    ...(CONCIERGE_VISIBLE ? [{
        id: 'ai-assistant',
        // The name people look for. It opens Bailey's front desk when the Bailey
        // tab is on (the route redirects there).
        label: 'AI Assistant',
        path: '/settings/ai-assistant',
        // Whoever has `settings`, exactly as the server checks it (audit
        // 2026-09-29 P1-7). NOT self-employed by default: the assistant is the
        // salon's, and its inbox holds other stylists' clients' conversations.
        roles: ['owner'],
        cap: 'settings',
        capOnly: true, // follow can('settings') whether or not the permissions flag is on
        receptionKey: 'aiReceptionist',
        hideInReception: true,
    }] : []),
    {
        id: 'finish-setup',
        label: 'Finish setting up',
        path: '/settings/finish-setup',
        roles: ['owner', 'selfEmployed'],
        receptionKey: 'finishSetup',
    },
    ...(ONBOARDING_DEMO_VISIBLE ? [{
        id: 'onboarding',
        label: 'Onboarding',
        path: '/settings/onboarding',
        roles: ['owner'],
        receptionKey: null,
    }, {
        id: 'bailey-welcome',
        label: "Bailey's welcome",
        path: '/settings/bailey-welcome',
        roles: ['owner'],
        receptionKey: null,
    }, {
        id: 'diary-demo',
        label: 'Diary demo',
        path: '/settings/diary-demo',
        roles: ['owner'],
        receptionKey: null,
    }] : []),
    ...(SAVINGS_CALCULATOR_VISIBLE ? [{
        id: 'savings-calculator',
        label: 'Savings calculator',
        path: '/settings/savings-calculator',
        roles: ['owner'],
        receptionKey: null,
    }] : []),
];

function getReceptionSettings() {
    try {
        const cached = localStorage.getItem('receptionModeSettings');
        if (cached) return JSON.parse(cached);
    } catch (e) { /* ignore */ }
    return { enabled: false, hiddenAreas: {} };
}

// The signed-in record saved at login, for when the store has no user yet
// (or lost it): without a role this page would show nothing at all.
function storedHairdresser() {
    try {
        const u = JSON.parse(localStorage.getItem('user')) || null;
        return (u && (u.hairdresser || (u.userInfo && u.userInfo.hairdresser))) || null;
    } catch (e) { return null; }
}

function getUserRole(user) {
    const hairdresser = user?.userInfo?.hairdresser || user?.hairdresserInfo || storedHairdresser();
    if (!hairdresser) return null;
    if (hairdresser.isSalonOwner === 1) return 'owner';
    if (hairdresser.employmentTypeId === 2) return 'selfEmployed';
    return 'employed';
}

/**
 * Does this tab open for the signed-in person?
 *   roles: 'all' | 'owner' (account holder / owner here) | 'groupOwner'
 *          (owner of the whole business) | 'selfEmployed' (their own book)
 *   cap:   or anyone whose role has this capability
 *   capOnly: the capability alone decides
 * Owner comes from the server's resolved access (/permissions/me), falling
 * back to the account type until it lands — so someone nobody gave a role
 * sees exactly the tabs their account type always had.
 */
function tabAllowed(tab, role) {
    if (tab.capOnly) return can(tab.cap);
    if (tab.roles.includes('all')) return true;
    const owner = !!getAccess().owner;
    if (tab.roles.includes('owner') && owner) return true;
    if (tab.roles.includes('groupOwner') && isGroupOwnerHere()) return true;
    if (tab.roles.includes('selfEmployed') && role === 'selfEmployed') return true;
    return !!(tab.cap && can(tab.cap));
}

function filterTabs(role, receptionSettings) {
    if (!role) return [];
    const { enabled, hiddenAreas } = receptionSettings;

    return ALL_TABS.filter(tab => {
        if (!tabAllowed(tab, role)) return false;

        // Reception mode check
        if (enabled && tab.receptionKey && hiddenAreas && hiddenAreas[tab.receptionKey]) {
            return false;
        }
        if (enabled && tab.hideInReception) return false;

        return true;
    });
}

export default function useSettingsTabs(user) {
    useAccess(); // re-filter when /permissions/me lands or the location changes
    const [receptionSettings, setReceptionSettings] = useState(getReceptionSettings);
    const role = getUserRole(user);
    const tabs = filterTabs(role, receptionSettings);

    const handleReceptionChange = useCallback((event) => {
        if (event.detail) {
            const { enabled, hiddenAreas } = event.detail;
            setReceptionSettings({ enabled, hiddenAreas: hiddenAreas || {} });
        }
    }, []);

    useEffect(() => {
        window.addEventListener('receptionModeSettingsChanged', handleReceptionChange);
        return () => window.removeEventListener('receptionModeSettingsChanged', handleReceptionChange);
    }, [handleReceptionChange]);

    return { tabs, loading: !role };
}

export { ALL_TABS, getUserRole, filterTabs, tabAllowed };
